import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { rethrowUnlessAbort } from "../../utils/fetch-error.util"
import { flashFileWarning, partitionByAccept } from "../../utils/file-upload.util"
import { pluralText } from "../../utils/plural-category.util"

const DEFAULT_TEXTS = {
  outcomes: {
    fed: {
      pdf: "prečítame a porovnáme s bankou",
      xml: "doplníme podľa ID z banky a porovnáme",
      csv: "namapuješ stĺpce, potom porovnáme s bankou"
    },
    unfed: {
      pdf: "prečítame a naimportujeme",
      xml: "naimportujeme",
      csv: "namapuješ stĺpce a naimportujeme"
    },
    unknown: {
      pdf: "podľa účtu z výpisu",
      xml: "podľa účtu z výpisu",
      csv: "podľa účtu z výpisu"
    }
  },
  submit: {
    one: "Nahrať %{count} súbor",
    few: "Nahrať %{count} súbory",
    other: "Nahrať %{count} súborov"
  },
  submitEmpty: "Nahrať",
  remove: "Odstrániť",
  unsupportedFile: "Súbor má nepodporovaný formát a bol preskočený: "
}

// Connects to data-controller="bank--statement-import"
// Handles single dropzone, keyboard activation, chosen files list preview with outcomes, submit state.
export default class extends Controller {
  static targets = [
    "fileInput",
    "dropZone",
    "fileList",
    "fileTemplate",
    "submitButton",
    "submitLabel",
    "emptyState"
  ]

  static values = {
    mode: { type: String, default: "unknown" },
    texts: { type: Object, default: {} }
  }

  get #texts() {
    return {
      ...DEFAULT_TEXTS,
      ...this.textsValue,
      outcomes: {
        fed: { ...DEFAULT_TEXTS.outcomes.fed, ...(this.textsValue.outcomes?.fed || {}) },
        unfed: { ...DEFAULT_TEXTS.outcomes.unfed, ...(this.textsValue.outcomes?.unfed || {}) },
        unknown: { ...DEFAULT_TEXTS.outcomes.unknown, ...(this.textsValue.outcomes?.unknown || {}) }
      },
      submit: {
        ...DEFAULT_TEXTS.submit,
        ...(this.textsValue.submit || {})
      }
    }
  }

  connect() {
    this._files = new DataTransfer()
    this.updateUI()
  }

  // Open native file picker
  openFilePicker(event) {
    if (event?.target === this.fileInputTarget) return
    this.fileInputTarget.click()
  }

  // Keyboard accessibility on the dropzone
  handleDropzoneKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.openFilePicker(event)
    }
  }

  // Handle file input change
  handleFiles() {
    this.addFiles(Array.from(this.fileInputTarget.files))
    this.fileInputTarget.value = ""
  }

  addFiles(incomingFiles) {
    const { accepted, rejected } = partitionByAccept(incomingFiles, this.fileInputTarget.accept)

    for (const file of rejected) {
      const prefix = this.#texts.unsupportedFile || "Súbor má nepodporovaný formát a bol preskočený: "
      flashFileWarning(`${prefix}${file.name}`)
    }

    for (const file of accepted) {
      this._files.items.add(file)
    }

    this.updateUI()
  }

  // Drag-and-drop handlers
  dragOver(event) {
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("file-drop--active")
    }
  }

  dragLeave(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("file-drop--active")
    }
  }

  drop(event) {
    event.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("file-drop--active")
    }

    const droppedFiles = Array.from(event.dataTransfer?.files || [])
    this.addFiles(droppedFiles)
  }

  // Remove a file from the list
  removeFile(event) {
    const el = event.target.closest("[data-index]") || event.currentTarget
    const index = parseInt(el?.dataset?.index ?? event.params?.index, 10)
    if (Number.isNaN(index)) return

    const newDt = new DataTransfer()
    for (let i = 0; i < this._files.files.length; i++) {
      if (i !== index) newDt.items.add(this._files.files[i])
    }
    this._files = newDt
    this.updateUI()
  }

  #fileType(file) {
    const name = (file.name || "").toLowerCase()
    if (name.endsWith(".pdf")) return "pdf"
    if (name.endsWith(".xml")) return "xml"
    if (name.endsWith(".csv")) return "csv"
    return "unknown"
  }

  #outcomeText(file) {
    const type = this.#fileType(file)
    const mode = this.modeValue || "unknown"
    const outcomes = this.#texts.outcomes || {}
    const modeOutcomes = outcomes[mode] || outcomes.unknown || {}
    return modeOutcomes[type] || modeOutcomes.all || ""
  }

  #typeIcon(type) {
    switch (type) {
      case "pdf":
        return "picture_as_pdf"
      case "csv":
        return "table_view"
      case "xml":
        return "code"
      default:
        return "description"
    }
  }

  // Submit the form with accumulated files via fetch + FormData
  async submit(event) {
    event.preventDefault()
    if (this._files.files.length === 0) return

    const form = this.element.querySelector("form")
    if (!form) return

    // Show loading state
    this.submitButtonTarget.disabled = true
    const originalHTML = this.submitButtonTarget.innerHTML
    this.submitButtonTarget.innerHTML =
      '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>' +
      this.submitButtonTarget.dataset.loadingText

    // Build FormData with accumulated files + hidden inputs from the form
    const formData = new FormData()
    for (const file of this._files.files) {
      formData.append("files[]", file)
    }
    form.querySelectorAll("input[type=hidden]").forEach((input) => {
      formData.append(input.name, input.value)
    })

    const csrfToken = document.querySelector('[name="csrf-token"]')?.content

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html"
        },
        body: formData
      })

      const text = await response.text()
      if (text) Turbo.renderStreamMessage(text)
      // The flash and list arrive either way; the modal may be gone by now (a Turbo visit).
      if (!this.element.isConnected) return
      if (response.ok) {
        this.#closeModal()
      } else {
        this.#resetSubmitButton(originalHTML)
      }
    } catch (error) {
      if (this.element.isConnected) this.#resetSubmitButton(originalHTML)
      rethrowUnlessAbort(error)
    }
  }

  #closeModal() {
    const modalEl = this.element.closest(".modal")
    if (modalEl) {
      const modal = Modal.getInstance(modalEl)
      if (modal) modal.hide()
    }
  }

  #resetSubmitButton(originalHTML) {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.innerHTML = originalHTML
  }

  // Update file list and button state
  updateUI() {
    if (!this._files) return

    const files = Array.from(this._files.files)
    const hasFiles = files.length > 0

    // Toggle empty state vs file list
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("d-none", hasFiles)
    }

    // Render file list from template
    if (this.hasFileListTarget) {
      this.fileListTarget.classList.toggle("d-none", !hasFiles)
      this.fileListTarget.replaceChildren()

      if (hasFiles && this.hasFileTemplateTarget) {
        files.forEach((file, index) => {
          const clone = this.fileTemplateTarget.content.firstElementChild.cloneNode(true)

          const nameEl = clone.querySelector('[data-target="name"]')
          if (nameEl) nameEl.textContent = file.name

          const sizeEl = clone.querySelector('[data-target="size"]')
          if (sizeEl) sizeEl.textContent = this.formatSize(file.size)

          const outcomeEl = clone.querySelector('[data-target="outcome"]')
          if (outcomeEl) outcomeEl.textContent = this.#outcomeText(file)

          const iconEl = clone.querySelector('[data-target="icon"]')
          if (iconEl) {
            iconEl.textContent = this.#typeIcon(this.#fileType(file))
          }

          const removeBtn = clone.querySelector('[data-target="remove"]')
          if (removeBtn) {
            removeBtn.dataset.index = index
            if (this.#texts.remove) {
              removeBtn.setAttribute("aria-label", this.#texts.remove)
            }
          }

          this.fileListTarget.appendChild(clone)
        })
      }
    }

    // Submit button state and label
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !hasFiles
      const count = files.length
      const lang = document.documentElement.lang || "sk"
      const label =
        count === 0
          ? (this.#texts.submitEmpty || "Nahrať")
          : pluralText(this.#texts.submit, lang, count).replace("%{count}", count)

      if (this.hasSubmitLabelTarget) {
        this.submitLabelTarget.textContent = label
      } else {
        this.submitButtonTarget.innerHTML = `<span class="material-symbols-outlined me-1" style="font-size: 18px;">upload_file</span>${label}`
      }
    }
  }

  formatSize(bytes) {
    if (bytes === 0) return "0 B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    const formatted = (bytes / Math.pow(k, i)).toFixed(1).replace(".", ",")
    return `${formatted} ${sizes[i]}`
  }
}
